import React from 'react';
import { useTranslation } from 'react-i18next';
import { AdminVideo } from '../common/model/dto/video/admin-video';
import { VideoPlacement } from '../common/model/dto/video/video-placement';
import { RecordColumn } from '../common/model/ui/record-list.type';
import OvPillLink from '../components/UI/atoms/OvPillLink';
import OvStackedDateTime from '../components/UI/atoms/OvStackedDateTime';
import OvVideoEditOrderButton from '../components/UI/molecules/OvVideoEditOrderButton';
import OvVideoShownIn from '../components/UI/molecules/OvVideoShownIn';
import OvVideoStatusSummary from '../components/UI/molecules/OvVideoStatusSummary';
import OvVideoSummary from '../components/UI/molecules/OvVideoSummary';
import { isOrderEditable } from '../common/utils/video-placement-utils';
import { useVideoUploads } from './use-video-uploads';

interface VideoListColumnsOptions {
  placements: VideoPlacement[] | undefined;
  isUnseen: (video: AdminVideo) => boolean;
  onEditOrder?: (video: AdminVideo) => void;
}

export function useVideoListColumns({
  placements,
  isUnseen,
  onEditOrder,
}: VideoListColumnsOptions): RecordColumn<AdminVideo>[] {
  const { t } = useTranslation();
  const { uploads } = useVideoUploads();
  const uploadOf = (video: AdminVideo) =>
    uploads.find(({ videoId }) => videoId === video.id);

  return [
    {
      id: 'video',
      header: t('videos.fields.video'),
      sortKey: 'title',
      renderCell: (video, detailsPath = '') => (
        <OvVideoSummary
          video={video}
          detailsPath={detailsPath}
          isUnseen={isUnseen(video)}
        />
      ),
    },
    {
      id: 'status',
      header: t('videos.fields.status'),
      renderCell: (video) => (
        <OvVideoStatusSummary video={video} upload={uploadOf(video)} />
      ),
    },
    {
      id: 'shownIn',
      header: t('videos.fields.shownIn'),
      renderCell: (video) => (
        <OvVideoShownIn
          video={video}
          placements={placements}
          upload={uploadOf(video)}
        >
          {onEditOrder && isOrderEditable(video) && (
            <OvVideoEditOrderButton video={video} onEdit={onEditOrder} />
          )}
        </OvVideoShownIn>
      ),
    },
    {
      id: 'created',
      header: t('videos.fields.createdAt'),
      sortKey: 'created_at',
      renderCell: (video) => <OvStackedDateTime value={video.created_at} />,
    },
    {
      id: 'action',
      header: t('videos.list.actions'),
      isHeaderHidden: true,
      renderCell: (video, detailsPath = '') => (
        <OvPillLink
          to={detailsPath}
          aria-label={t('videos.list.detailsFor', { title: video.title })}
        >
          {t('videos.list.details')}
        </OvPillLink>
      ),
    },
  ];
}
